import React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormLabel,
  Divider,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/material';
import { AccountCircle, Settings as SettingsIcon, Check, Edit, VolumeUp, Menu as MenuIcon, CreditCard as CreditCardIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useThemeContext } from '../context/ThemeContext';
import { useSidebar } from '../context/SidebarContext';
import AheadIcon from './AheadIcon';
import { playCompletionSound, soundOptions } from '../utils/completionSounds';

const themeSwatches = [
  { value: 'light', color: '#f7f8fa', border: '#bbb', label: 'Light' },
  { value: 'dark', color: '#181a1b', border: '#444', label: 'Dark' },
  { value: 'blue', color: '#1976d2', border: '#1976d2', label: 'Blue' },
  { value: 'green', color: '#388e3c', border: '#388e3c', label: 'Green' },
  { value: 'purple', color: '#7b1fa2', border: '#7b1fa2', label: 'Purple' },
  { value: 'orange', color: '#f57c00', border: '#f57c00', label: 'Orange' },
  { value: 'pink', color: '#d81b60', border: '#d81b60', label: 'Pink' },
  { value: 'ikb', color: '#002fa7', border: '#002fa7', label: 'IKB' },
  { value: 'lilac', color: '#b57edc', border: '#b57edc', label: 'Lilac' },
];

const Navbar = ({ isMinimal = false }) => {
  const { user, logout, loading: authLoading } = useAuth();
  const { themeName, setThemeName } = useThemeContext();
  const { sidebarCollapsed, toggleSidebar } = useSidebar();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [pwForm, setPwForm] = React.useState({ current: '', new: '', repeat: '' });
  const [pwLoading, setPwLoading] = React.useState(false);
  const [pwSuccess, setPwSuccess] = React.useState('');
  const [pwError, setPwError] = React.useState('');
  const [motto, setMotto] = React.useState('');
  const [editingMotto, setEditingMotto] = React.useState(false);
  const [mottoInput, setMottoInput] = React.useState('');
  const [selectedSound, setSelectedSound] = React.useState(() => localStorage.getItem('completion_sound') || 'chord');
  const [mottoLoading, setMottoLoading] = React.useState(false);
  const mottoFetchedRef = React.useRef(false); // Prevent duplicate fetches

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
    setAnchorEl(null);
  };
  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setPwForm({ current: '', new: '', repeat: '' });
    setPwSuccess('');
    setPwError('');
  };

  // Password reset handler
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    setPwSuccess('');
    setPwError('');
    
    if (pwForm.new !== pwForm.repeat) {
      setPwError('New passwords do not match.');
      setPwLoading(false);
      return;
    }
    
    if (!pwForm.current || !pwForm.new || pwForm.new.length < 6) {
      setPwError('Please enter your current password and a new password (min 6 chars).');
      setPwLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.post('/api/auth/change-password', {
        currentPassword: pwForm.current,
        newPassword: pwForm.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPwSuccess('Password updated successfully!');
      setPwForm({ current: '', new: '', repeat: '' });
    } catch (error) {
      setPwError(error.response?.data?.message || 'Failed to update password');
    } finally {
      setPwLoading(false);
    }
  };

  // Function to save motto to server (used for migration)
  const saveMottoToServer = React.useCallback(async (mottoValue) => {
    const valueToSave = mottoValue.trim();
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      await api.put('/api/auth/motto', 
        { motto: valueToSave },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Clear localStorage as we're now using server storage
      localStorage.removeItem('ahead_motto');
      return true;
    } catch (error) {
      console.error('Error saving motto:', error);
      return false;
    }
  }, []);

  const handleMottoSave = React.useCallback(async (mottoValue = null) => {
    const valueToSave = (mottoValue || mottoInput).trim();
    setMottoLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      await api.put('/api/auth/motto', 
        { motto: valueToSave },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMotto(valueToSave);
      setEditingMotto(false);
      // Clear localStorage as we're now using server storage
      localStorage.removeItem('ahead_motto');
      // Reset fetch flag so it can be fetched again if needed
      mottoFetchedRef.current = false;
    } catch (error) {
      console.error('Error saving motto:', error);
      // Fallback to localStorage on error
      localStorage.setItem('ahead_motto', valueToSave);
      setMotto(valueToSave);
      setEditingMotto(false);
    } finally {
      setMottoLoading(false);
    }
  }, [mottoInput]);

  // Fetch motto from API when user is available
  React.useEffect(() => {
    if (!user) {
      // Clear motto when user logs out
      setMotto('');
      setMottoInput('');
      mottoFetchedRef.current = false;
      return;
    }

    if (authLoading) {
      // Wait for auth to finish loading
      return;
    }

    // Prevent duplicate fetches
    if (mottoFetchedRef.current) {
      return;
    }

    // Check if user has an ID (MongoDB uses _id)
    const userId = user._id || user.id;
    if (!userId) {
      mottoFetchedRef.current = false; // Reset if no user ID
      return;
    }

    const fetchMotto = async () => {
      // Mark as fetching to prevent duplicates
      mottoFetchedRef.current = true;
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          mottoFetchedRef.current = false; // Reset on error
          return;
        }
        
        const response = await api.get('/api/auth/motto', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const fetchedMotto = response.data.motto || '';
        setMotto(fetchedMotto);
        setMottoInput(fetchedMotto);
        
        // If we have a local motto that's different, migrate it to server
        const localMotto = localStorage.getItem('ahead_motto');
        if (localMotto && localMotto !== fetchedMotto && fetchedMotto === '') {
          // Only migrate if server motto is empty and we have a local one
          // Do migration asynchronously to not block UI
          saveMottoToServer(localMotto).then(migrated => {
            if (migrated) {
              setMotto(localMotto);
              setMottoInput(localMotto);
            }
          }).catch(() => {
            // Silently fail migration
          });
        } else if (localMotto && localMotto !== fetchedMotto) {
          // If server has a different motto, use server version (server is source of truth)
          localStorage.removeItem('ahead_motto');
        }
      } catch (error) {
        console.error('Error fetching motto:', error);
        // Fallback to localStorage for backward compatibility
        const localMotto = localStorage.getItem('ahead_motto') || '';
        if (localMotto) {
          setMotto(localMotto);
          setMottoInput(localMotto);
          // Try to migrate to server (but don't wait for it)
          saveMottoToServer(localMotto).catch(() => {
            // Silently fail migration
          });
        } else {
          // Clear motto if no local storage and API fails
          setMotto('');
          setMottoInput('');
        }
        mottoFetchedRef.current = false; // Reset on error to allow retry
      }
    };
    
    fetchMotto();
  }, [user, authLoading, saveMottoToServer]); // Only re-fetch when user changes

  const handleSoundSelect = (soundType) => {
    setSelectedSound(soundType);
    localStorage.setItem('completion_sound', soundType);
    playCompletionSound(soundType);
  };

  return (
    <AppBar 
      position="static"
      elevation={0}
      sx={{
        ml: user ? { xs: 0, sm: sidebarCollapsed ? '60px' : '240px' } : 0,
        width: user ? { xs: '100%', sm: sidebarCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 240px)' } : '100%',
        transition: 'margin-left 0.3s ease, width 0.3s ease',
        background: (theme) => 
          theme.palette.mode === 'dark' 
            ? 'linear-gradient(180deg, rgba(30, 33, 36, 0.98) 0%, rgba(24, 26, 27, 0.98) 100%)'
            : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 251, 252, 0.98) 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: (theme) => 
            theme.palette.mode === 'dark' 
              ? 'none'
              : 'linear-gradient(180deg, rgba(39, 76, 202, 0.12) 0%, rgba(39, 76, 202, 0) 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderRadius: '0 0 16px 16px',
      }}
    >
      <Toolbar sx={{ py: 3, minHeight: '72px !important', position: 'relative', zIndex: 1 }}>
        {user && !isMinimal && (
          <IconButton
            onClick={toggleSidebar}
            sx={{
              mr: 2,
              color: 'text.primary',
              display: { xs: 'flex', sm: 'flex', md: 'none' },
              transition: 'all 80ms ease-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                bgcolor: 'action.hover',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          variant="h6"
          component={RouterLink}
          to={user ? "/app/tasks" : "/"}
          sx={{
            textDecoration: 'none',
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <AheadIcon size={28} style={{ marginRight: 6, verticalAlign: 'middle', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, letterSpacing: 1, flexShrink: 0 }}>Ahead</span>
          {!isMinimal && user && (
            <Box sx={{ ml: 3, minWidth: 0 }}>
              {editingMotto ? (
                <Box component="form" onSubmit={e => { e.preventDefault(); handleMottoSave(); }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    autoFocus
                    value={mottoInput}
                    onChange={e => setMottoInput(e.target.value)}
                    onBlur={() => handleMottoSave()}
                    maxLength={80}
                    disabled={mottoLoading}
                    style={{
                      fontSize: 16,
                      fontWeight: 400,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      width: 260,
                      padding: 2,
                      color: theme.palette.text.primary,
                      opacity: mottoLoading ? 0.6 : 1,
                    }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => handleMottoSave()} 
                    disabled={mottoLoading}
                    sx={{ ml: 0.5, color: 'text.primary' }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    color: motto ? 'text.secondary' : 'text.disabled',
                    fontSize: 16,
                    fontWeight: 400,
                    ml: 1,
                    minWidth: 0,
                    maxWidth: { xs: 120, sm: 320 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    transition: 'color 0.2s ease-out',
                    '&:hover': {
                      color: 'text.primary',
                    },
                  }}
                  onClick={() => { setMottoInput(motto); setEditingMotto(true); }}
                  title={motto || 'Click to add your favorite motto or quote'}
                >
                  {motto || 'Click to add your favorite motto or quote'}
                  <IconButton size="small" sx={{ ml: 0.5, color: 'inherit' }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </Typography>
        {!isMinimal && user && (
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
        )}
        {!isMinimal && !user && <Box sx={{ flexGrow: 1 }} />}
        {user && !isMinimal ? (
          <Box sx={{ flexShrink: 0 }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={{
                color: 'text.primary',
                transition: 'all 0.2s ease-out',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <Typography variant="body2">{user.name}</Typography>
              </MenuItem>
              <MenuItem onClick={handleSettingsOpen}>
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Settings
              </MenuItem>
              <MenuItem onClick={() => { handleClose(); navigate('/app/subscription'); }}>
                <CreditCardIcon fontSize="small" sx={{ mr: 1 }} /> Subscription
              </MenuItem>
              <MenuItem
                component="a"
                href="mailto:help@keepahead.app?subject=Ahead%20%E2%80%93%20feedback%20%2F%20support"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClose}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Feedback & Support
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
            {/* Settings Dialog */}
            <Dialog open={settingsOpen} onClose={handleSettingsClose} maxWidth="xs" fullWidth>
              <DialogTitle>Settings</DialogTitle>
              <DialogContent dividers>
                <Box component="form" onSubmit={handlePasswordReset} sx={{ mb: 3 }}>
                  <FormLabel sx={{ mb: 1 }}>Reset Password</FormLabel>
                  <TextField
                    label="Current Password"
                    type="password"
                    fullWidth
                    margin="dense"
                    value={pwForm.current}
                    onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    margin="dense"
                    value={pwForm.new}
                    onChange={e => setPwForm(f => ({ ...f, new: e.target.value }))}
                  />
                  <TextField
                    label="Repeat New Password"
                    type="password"
                    fullWidth
                    margin="dense"
                    value={pwForm.repeat}
                    onChange={e => setPwForm(f => ({ ...f, repeat: e.target.value }))}
                    error={!!pwForm.repeat && pwForm.new !== pwForm.repeat}
                    helperText={pwForm.repeat && pwForm.new !== pwForm.repeat ? 'Passwords do not match' : ''}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ mt: 2, borderRadius: 2 }}
                    disabled={pwLoading || !pwForm.current || !pwForm.new || !pwForm.repeat || pwForm.new !== pwForm.repeat}
                  >
                    {pwLoading ? 'Saving...' : 'Update Password'}
                  </Button>
                  {pwSuccess && <Alert severity="success" sx={{ mt: 2 }}>{pwSuccess}</Alert>}
                  {pwError && <Alert severity="error" sx={{ mt: 2 }}>{pwError}</Alert>}
                </Box>
                <Divider sx={{ my: 2 }} />
                <FormLabel sx={{ mb: 1 }}>Theme</FormLabel>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  {themeSwatches.map((swatch) => (
                    <Tooltip title={swatch.label} arrow>
                      <IconButton
                        key={swatch.value}
                        onClick={() => setThemeName(swatch.value)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: swatch.color,
                          border: themeName === swatch.value ? `2.5px solid ${swatch.border}` : '2.5px solid #eee',
                          boxShadow: themeName === swatch.value ? 2 : 0,
                          transition: 'border 0.2s, box-shadow 0.2s',
                          position: 'relative',
                          p: 0,
                        }}
                      >
                        {themeName === swatch.value && (
                          <Check sx={{ color: swatch.value === 'light' ? '#333' : '#fff', fontSize: 18, position: 'absolute', top: 4, left: 4 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Choose your preferred color theme.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <FormLabel sx={{ mb: 1 }}>Completion Sound</FormLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  {soundOptions.map((sound) => (
                    <Box
                      key={sound.value}
                      onClick={() => handleSoundSelect(sound.value)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: 2,
                        border: selectedSound === sound.value ? '2px solid' : '1px solid',
                        borderColor: selectedSound === sound.value ? 'primary.main' : 'divider',
                        bgcolor: selectedSound === sound.value ? 'action.selected' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <VolumeUp fontSize="small" color={selectedSound === sound.value ? 'primary' : 'disabled'} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: selectedSound === sound.value ? 600 : 400 }}>
                            {sound.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sound.description}
                          </Typography>
                        </Box>
                      </Box>
                      {selectedSound === sound.value && (
                        <Check color="primary" fontSize="small" />
                      )}
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Click a sound to preview and select it.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleSettingsClose}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        ) : (
          <Box>
            {isMinimal && location.pathname !== '/login' ? (
              <Button
                color="inherit"
                component={RouterLink}
                to="/login"
                sx={{ textTransform: 'none' }}
              >
                Log in
              </Button>
            ) : !isMinimal ? (
              <>
                <Button
                  color="inherit"
                  component={RouterLink}
                  to="/login"
                >
                  Login
                </Button>
                <Button
                  color="inherit"
                  component={RouterLink}
                  to="/register"
                >
                  Register
                </Button>
              </>
            ) : null}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 